import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Activity, TrendingUp, AlertCircle, Clock, Edit, TestTube, MoreVertical, X } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/Card';
import toast from 'react-hot-toast';

const APIManagementDashboard = () => {
  const { user } = useAuth();
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    internal_name: '',
    description: '',
    environment: 'production',
    base_url: '',
    health_endpoint: '',
    auth_type: 'none',
    auth_token: '',
    auth_key_name: '',
    auth_key_value: ''
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      loadAPIs();
    }
  }, [user]);

  const loadAPIs = async () => {
    try {
      const q = query(collection(db, 'API'), orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      const apiList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApis(apiList);
    } catch (error) {
      console.error('Failed to load APIs:', error);
      toast.error('Failed to load APIs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const authConfig = {
        type: formData.auth_type,
        ...(formData.auth_type === 'bearer' && { encrypted_token: btoa(formData.auth_token) }),
        ...(formData.auth_type === 'api-key' && { 
          key_name: formData.auth_key_name,
          encrypted_key: btoa(formData.auth_key_value)
        }),
        ...(formData.auth_type === 'basic' && { encrypted_token: btoa(formData.auth_token) })
      };

      await addDoc(collection(db, 'API'), {
        internal_name: formData.internal_name,
        description: formData.description,
        environment: formData.environment.toLowerCase(),
        base_url: formData.base_url,
        health_endpoint: formData.health_endpoint || '/health',
        auth_config: authConfig,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by_user_id: user.uid,
        current_status: 'healthy',
        last_health_check: serverTimestamp()
      });

      toast.success('API connection added successfully');
      setShowModal(false);
      resetForm();
      loadAPIs();
    } catch (error) {
      console.error('Failed to add API:', error);
      toast.error('Failed to add API connection');
    }
  };

  const resetForm = () => {
    setFormData({
      internal_name: '',
      description: '',
      environment: 'production',
      base_url: '',
      health_endpoint: '',
      auth_type: 'none',
      auth_token: '',
      auth_key_name: '',
      auth_key_value: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'degraded': return 'bg-yellow-500';
      case 'down': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const filteredAPIs = apis.filter(api => 
    api.internal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    api.base_url?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate metrics
  const totalAPIs = apis.length;
  const healthyAPIs = apis.filter(a => a.current_status === 'healthy').length;
  const errorRate = totalAPIs > 0 ? ((totalAPIs - healthyAPIs) / totalAPIs * 100).toFixed(2) : 0;

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Top Section: Compact Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">System Status</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {healthyAPIs === totalAPIs ? '✓ Operational' : `${totalAPIs - healthyAPIs} Issues`}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${healthyAPIs === totalAPIs ? 'bg-green-100' : 'bg-red-100'}`}>
                {healthyAPIs === totalAPIs ? (
                  <span className="text-2xl text-green-600">✓</span>
                ) : (
                  <span className="text-2xl text-red-600">✗</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Total APIs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalAPIs}</p>
              </div>
              <Activity className="w-8 h-8 text-cobalt-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Error Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{errorRate}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Healthy APIs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{healthyAPIs}/{totalAPIs}</p>
              </div>
              <Clock className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Middle Section: Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search connected APIs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="ml-4 flex items-center space-x-2 px-4 py-2 bg-cobalt-500 text-white rounded-lg hover:bg-cobalt-600 transition-colors font-semibold"
        >
          <Plus className="w-5 h-5" />
          <span>Add New API</span>
        </button>
      </div>

      {/* Bottom Section: APIs Table */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Internal Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Environment</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Base Endpoint URL</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Auth Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">Loading APIs...</td>
                  </tr>
                ) : filteredAPIs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      {searchTerm ? 'No APIs found matching your search' : 'No APIs connected yet. Click "Add New API" to get started.'}
                    </td>
                  </tr>
                ) : (
                  filteredAPIs.map((api) => (
                    <tr key={api.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(api.current_status)}`} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">{api.internal_name}</div>
                        <div className="text-xs text-gray-500">{api.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          api.environment === 'production' ? 'bg-green-100 text-green-800' :
                          api.environment === 'staging' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {api.environment}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-mono">{api.base_url}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 capitalize">{api.auth_config?.type || 'none'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button className="p-1 text-gray-400 hover:text-cobalt-500 transition-colors">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-green-500 transition-colors">
                            <TestTube className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add API Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Add New API Connection</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Core Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Core Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.internal_name}
                      onChange={(e) => setFormData({ ...formData, internal_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                      placeholder="e.g., Production Shipping Partner"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                      rows="2"
                      placeholder="Short description of what this API does"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Environment <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.environment}
                      onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                    >
                      <option value="production">Production</option>
                      <option value="staging">Staging</option>
                      <option value="development">Development</option>
                      <option value="sandbox">Sandbox</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Connection Endpoint */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Connection Endpoint</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.base_url}
                      onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                      placeholder="https://api.myservice.com/v2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Health Check Endpoint</label>
                    <input
                      type="text"
                      value={formData.health_endpoint}
                      onChange={(e) => setFormData({ ...formData, health_endpoint: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                      placeholder="/healthz or /status"
                    />
                  </div>
                </div>
              </div>

              {/* Authentication */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Authentication</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Auth Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.auth_type}
                      onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                    >
                      <option value="none">No Auth</option>
                      <option value="bearer">Bearer Token</option>
                      <option value="api-key">X-API-Key Header</option>
                      <option value="basic">Basic Auth</option>
                    </select>
                  </div>

                  {formData.auth_type === 'bearer' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Token Value <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        required
                        value={formData.auth_token}
                        onChange={(e) => setFormData({ ...formData, auth_token: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                        placeholder="Enter bearer token"
                      />
                    </div>
                  )}

                  {formData.auth_type === 'api-key' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Key Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.auth_key_name}
                          onChange={(e) => setFormData({ ...formData, auth_key_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                          placeholder="e.g., X-Service-ID"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Key Value <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          required
                          value={formData.auth_key_value}
                          onChange={(e) => setFormData({ ...formData, auth_key_value: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                          placeholder="Enter API key value"
                        />
                      </div>
                    </>
                  )}

                  {formData.auth_type === 'basic' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Credentials (username:password) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        required
                        value={formData.auth_token}
                        onChange={(e) => setFormData({ ...formData, auth_token: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                        placeholder="username:password"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-cobalt-500 text-white rounded-lg hover:bg-cobalt-600 transition-colors font-semibold"
                >
                  Save Connection
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default APIManagementDashboard;
